import React, { useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { useWorldStore, FogType, ParticleType } from '../stores/worldStore';
import { useDraggable } from '../hooks/useDraggable';
import './AtmosphereControls.css';

const FOG_OPTIONS: { value: FogType; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'linear', label: 'Linear' },
  { value: 'exponential', label: 'Exponential' },
];

const PARTICLE_OPTIONS: { value: ParticleType; label: string; icon: string }[] = [
  { value: 'none', label: 'None', icon: '—' },
  { value: 'rain', label: 'Rain', icon: '🌧' },
  { value: 'snow', label: 'Snow', icon: '❄' },
  { value: 'dust', label: 'Dust', icon: '💨' },
  { value: 'fireflies', label: 'Fireflies', icon: '✨' },
  { value: 'embers', label: 'Embers', icon: '🔥' },
  { value: 'bubbles', label: 'Bubbles', icon: '🫧' },
];

const AtmosphereControls: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const atmosphere = useWorldStore((s) => s.atmosphere);
  const setAtmosphere = useWorldStore((s) => s.setAtmosphere);
  const { dragStyle, onPointerDown } = useDraggable();
  const panelRef = useRef<HTMLDivElement>(null);

  // Block ALL native pointer/mouse/wheel events from reaching the canvas/OrbitControls
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const stop = (e: Event) => e.stopPropagation();
    const events = ['pointerdown', 'pointermove', 'pointerup', 'mousedown', 'mousemove', 'mouseup', 'wheel', 'touchstart', 'touchmove', 'touchend'];
    events.forEach((evt) => el.addEventListener(evt, stop, { capture: true }));
    return () => { events.forEach((evt) => el.removeEventListener(evt, stop, { capture: true })); };
  }, []);

  return (
    <div ref={panelRef} className="atmosphere-controls" style={dragStyle} onPointerDown={(e) => { e.stopPropagation(); onPointerDown(e); }} onMouseDown={(e) => e.stopPropagation()}>
      <div className="controls-header" data-drag-handle>
        <h3>Atmosphere</h3>
        <button className="icon-btn" onClick={onClose} title="Close"><X size={18} /></button>
      </div>

      <div className="controls-scroll">
        {/* Fog */}
        <div className="control-group">
          <label>Fog</label>
          <div className="toggle-row">
            {FOG_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`toggle-btn ${atmosphere.fog === opt.value ? 'active' : ''}`}
                onClick={() => setAtmosphere({ fog: opt.value })}
              >{opt.label}</button>
            ))}
          </div>
          {atmosphere.fog !== 'none' && (
            <>
              <div className="control-row">
                <span>Color</span>
                <input type="color" value={atmosphere.fogColor} onChange={(e) => setAtmosphere({ fogColor: e.target.value })} />
              </div>
              {atmosphere.fog === 'linear' && (
                <>
                  <div className="control-row">
                    <span>Near</span>
                    <input type="range" min={1} max={100} step={1} value={atmosphere.fogNear} onChange={(e) => setAtmosphere({ fogNear: +e.target.value })} />
                    <span className="value">{atmosphere.fogNear}</span>
                  </div>
                  <div className="control-row">
                    <span>Far</span>
                    <input type="range" min={10} max={500} step={5} value={atmosphere.fogFar} onChange={(e) => setAtmosphere({ fogFar: +e.target.value })} />
                    <span className="value">{atmosphere.fogFar}</span>
                  </div>
                </>
              )}
              {atmosphere.fog === 'exponential' && (
                <div className="control-row">
                  <span>Density</span>
                  <input type="range" min={0.001} max={0.1} step={0.001} value={atmosphere.fogDensity} onChange={(e) => setAtmosphere({ fogDensity: +e.target.value })} />
                  <span className="value">{atmosphere.fogDensity.toFixed(3)}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Particles */}
        <div className="control-group">
          <label>Particles</label>
          <div className="particle-grid">
            {PARTICLE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`particle-btn ${atmosphere.particles === opt.value ? 'active' : ''}`}
                onClick={() => setAtmosphere({ particles: opt.value })}
                title={opt.label}
              >
                <span className="particle-icon">{opt.icon}</span>
                <span className="particle-label">{opt.label}</span>
              </button>
            ))}
          </div>
          {atmosphere.particles !== 'none' && (
            <>
              <div className="control-row">
                <span>Count</span>
                <input type="range" min={50} max={2000} step={50} value={atmosphere.particleCount} onChange={(e) => setAtmosphere({ particleCount: +e.target.value })} />
                <span className="value">{atmosphere.particleCount}</span>
              </div>
              <div className="control-row">
                <span>Color</span>
                <input type="color" value={atmosphere.particleColor} onChange={(e) => setAtmosphere({ particleColor: e.target.value })} />
              </div>
            </>
          )}
        </div>

        {/* Bloom */}
        <div className="control-group">
          <label>Bloom</label>
          <div className="control-row">
            <span>Enable</span>
            <button
              className={`toggle-switch ${atmosphere.bloom ? 'on' : ''}`}
              onClick={() => setAtmosphere({ bloom: !atmosphere.bloom })}
            >
              <span className="toggle-knob" />
            </button>
          </div>
          {atmosphere.bloom && (
            <>
              <div className="control-row">
                <span>Intensity</span>
                <input type="range" min={0} max={3} step={0.1} value={atmosphere.bloomIntensity} onChange={(e) => setAtmosphere({ bloomIntensity: +e.target.value })} />
                <span className="value">{atmosphere.bloomIntensity.toFixed(1)}</span>
              </div>
              <div className="control-row">
                <span>Threshold</span>
                <input type="range" min={0} max={1} step={0.05} value={atmosphere.bloomThreshold} onChange={(e) => setAtmosphere({ bloomThreshold: +e.target.value })} />
                <span className="value">{atmosphere.bloomThreshold.toFixed(2)}</span>
              </div>
            </>
          )}
        </div>

        {/* Lighting */}
        <div className="control-group">
          <label>Lighting</label>
          <div className="control-row">
            <span>Ambient</span>
            <input type="range" min={0} max={2} step={0.05} value={atmosphere.ambientIntensity} onChange={(e) => setAtmosphere({ ambientIntensity: +e.target.value })} />
            <span className="value">{atmosphere.ambientIntensity.toFixed(2)}</span>
          </div>
          <div className="control-row">
            <span>Sun</span>
            <input type="range" min={0} max={3} step={0.1} value={atmosphere.sunIntensity} onChange={(e) => setAtmosphere({ sunIntensity: +e.target.value })} />
            <span className="value">{atmosphere.sunIntensity.toFixed(1)}</span>
          </div>
          <div className="control-row">
            <span>Color</span>
            <input type="color" value={atmosphere.sunColor} onChange={(e) => setAtmosphere({ sunColor: e.target.value })} />
          </div>
          <div className="control-row">
            <span>Sun shadows</span>
            <button
              className={`toggle-switch ${atmosphere.sunShadows ? 'on' : ''}`}
              onClick={() => setAtmosphere({ sunShadows: !atmosphere.sunShadows })}
            >
              <span className="toggle-knob" />
            </button>
          </div>
          {atmosphere.sunShadows && (
            <div className="control-hint" style={{ fontSize: 11, opacity: 0.65, marginTop: 4 }}>
              Ceilings and walls now block sunlight — lower Ambient for truly dark interiors. Costs GPU on mobile/Quest.
            </div>
          )}
        </div>

        <button className="reset-btn" onClick={() => setAtmosphere({
          fog: 'none', fogColor: '#cccccc', fogNear: 10, fogFar: 100, fogDensity: 0.02,
          particles: 'none', particleCount: 500, particleColor: '#ffffff',
          bloom: false, bloomIntensity: 0.5, bloomThreshold: 0.8,
          ambientIntensity: 0.4, sunIntensity: 1.0, sunColor: '#ffffff', sunShadows: false,
        })}>Reset to Defaults</button>
      </div>
    </div>
  );
};

export default AtmosphereControls;
